// useUrlScan.ts — lógica del scan SSE del onboarding de Novah.
// Qué hace: dispara el scrape de la URL vía apiClient.stream, maneja la cola de
//   mensajes para una cadencia natural de chat, deduplica fases y dirige los
//   steps (logo-select / brand-name) según lo que el scraper encuentre.
// Qué exporta: useUrlScan (devuelve { handleScan }).
// Quién lo usa: CreateWorkspaceModal.

import { MutableRefObject } from 'react';
import { ClassifiedImage } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import { isValidUrl } from '../../components/createWorkspace/styled';
import { WizardStep, ScanResult } from '../../components/createWorkspace/types';

interface Deps {
  url: string;
  setUrl: (v: string) => void;
  brandName: string;
  setBrandName: (v: string) => void;
  scanResultRef: MutableRefObject<ScanResult | null>;
  setScanResult: React.Dispatch<React.SetStateAction<ScanResult | null>>;
  setStep: React.Dispatch<React.SetStateAction<WizardStep>>;
  pushStep: (next: WizardStep) => void;
  user: (text: string) => void;
  addMsg: (msg: { from: 'novah' | 'user'; text: string }) => void;
  analyzeTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
}

export function useUrlScan(deps: Deps) {
  const {
    url, setUrl, brandName, setBrandName, scanResultRef, setScanResult,
    setStep, pushStep, user, addMsg, analyzeTimeoutRef,
  } = deps;

  const handleScan = async () => {
    if (!isValidUrl(url)) return;
    // Normalizar: si no puso scheme, asumimos https:// para que el scrape no falle.
    const target = /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
    if (target !== url) setUrl(target);
    user(target);
    setStep('scanning');

    addMsg({ from: 'novah', text: 'Let me take a look at your website...' });

    // Message queue for natural chat cadence
    const messageQueue: Array<{ text: string; action?: () => void }> = [];
    let queueRunning = false;
    const processQueue = () => {
      if (queueRunning || messageQueue.length === 0) return;
      queueRunning = true;
      const item = messageQueue.shift()!;
      setTimeout(() => {
        addMsg({ from: 'novah', text: item.text });
        item.action?.();
        queueRunning = false;
        processQueue();
      }, 600);
    };
    const enqueue = (text: string, action?: () => void) => {
      messageQueue.push({ text, action });
      processQueue();
    };

    // Track shown phases to deduplicate
    const shownPhases = new Set<string>();

    try {
      await apiClient.stream('/api/workspaces/scan-url', { url: target }, (event, data) => {
        const d = data as Record<string, unknown>;

        if (event === 'progress') {
          const s = d.step as string;

          if (s === 'extracting' && d.fetchStrategy && !shownPhases.has('extracted')) {
            shownPhases.add('extracted');
            const strategy = d.fetchStrategy as string;
            const strategyLabel = strategy === 'direct' ? 'Direct fetch' : strategy === 'googlebot' ? 'Googlebot' : 'Google Cache';
            const extra: string[] = [];
            if (d.usedFrameworkData) extra.push('Next.js data');
            if (d.usedJina) extra.push('Jina Reader');
            enqueue(`Website fetched via ${strategyLabel}${extra.length ? ' + ' + extra.join(', ') : ''} ✓`);

            const name = d.suggestedName as string;
            const logos = d.logoCount as number;
            const photos = d.photoCount as number;
            const colors = d.colorCount as number;
            const parts: string[] = [];
            if (name) parts.push(`Brand detected: **${name}**`);
            if (logos) parts.push(`${logos} logo${logos > 1 ? 's' : ''}`);
            if (photos) parts.push(`${photos} photo${photos > 1 ? 's' : ''}`);
            if (colors) parts.push(`${colors} brand color${colors > 1 ? 's' : ''}`);
            if (parts.length > 0) {
              enqueue(parts.join(' · '));
            }

            // Store images early so user can pick logo/photos while AI analyzes
            if (d.images && (d.images as ClassifiedImage[]).length > 0) {
              const earlyResult: ScanResult = {
                url: d.url as string || url.trim(),
                suggestedName: name || '',
                images: d.images as ClassifiedImage[],
                brandColors: (d.brandColors as string[]) || [],
                brandFonts: (d.brandFonts as string[]) || [],
                brandInfo: {},
              };
              setScanResult(earlyResult);
              scanResultRef.current = earlyResult;
              if (name) setBrandName(name);
            }
          }

          // When AI starts analyzing, skip to images immediately
          // Brand name is already set from extraction — go straight to logo selection
          if (s === 'analyzing' && !shownPhases.has('analyzing')) {
            shownPhases.add('analyzing');
            const hasImages = (scanResultRef.current?.images || []).length > 0;
            if (hasImages) {
              const imgCount = scanResultRef.current?.images.length || 0;
              enqueue(`I found ${imgCount} images. Which one is your logo? Pick one or skip.`, () => {
                // Force re-sync state from ref before showing logo-select
                // This ensures the UI renders with the images that are in the ref
                setScanResult(prev => prev || scanResultRef.current || prev);
                requestAnimationFrame(() => setStep('logo-select'));
              });
            } else {
              // No images found — skip logo/photo selection, confirm brand name
              enqueue("No images found. Let's confirm your brand name.", () => {
                pushStep('brand-name');
              });
            }

            if (analyzeTimeoutRef.current) clearTimeout(analyzeTimeoutRef.current);
            analyzeTimeoutRef.current = setTimeout(() => {
              // AI took too long — we already have images, just continue
              if (analyzeTimeoutRef.current) { analyzeTimeoutRef.current = null; }
            }, 90000);
          }
        }

        // AI analysis complete — silently store brand info (user is already picking images)
        if (event === 'complete') {
          if (analyzeTimeoutRef.current) { clearTimeout(analyzeTimeoutRef.current); analyzeTimeoutRef.current = null; }
          const completeResult: ScanResult = {
            url: d.url as string || scanResultRef.current?.url || '',
            suggestedName: d.suggestedName as string || scanResultRef.current?.suggestedName || '',
            images: (d.images as ClassifiedImage[]) || scanResultRef.current?.images || [],
            brandColors: (d.brandColors as string[]) || scanResultRef.current?.brandColors || [],
            brandFonts: (d.brandFonts as string[]) || scanResultRef.current?.brandFonts || [],
            brandInfo: d.brandInfo as Record<string, unknown> || scanResultRef.current?.brandInfo || {},
          };
          setScanResult(completeResult);
          scanResultRef.current = completeResult;
          if (d.suggestedName && !brandName) setBrandName(d.suggestedName as string);
        }

        if (event === 'error') {
          if (analyzeTimeoutRef.current) { clearTimeout(analyzeTimeoutRef.current); analyzeTimeoutRef.current = null; }
          // Clear queue and show error immediately
          messageQueue.length = 0;
          addMsg({ from: 'novah', text: `Oops: ${d.message || 'Scan failed'}. Try again or create manually.` });
          setStep('greeting');
        }
      });
    } catch (err) {
      messageQueue.length = 0;
      addMsg({ from: 'novah', text: `Failed to scan: ${err instanceof Error ? err.message : 'Unknown error'}. Try again or create manually.` });
      setStep('greeting');
    }
  };

  return { handleScan };
}
